import React from "react";
import { QRCodeSVG } from "qrcode.react";
import CircularProgress from "@mui/material/CircularProgress";
import type {
  NegotiationPendingTopup,
  NegotiationTopup,
  NegotiationTopupFee,
  NegotiationTopupLimits,
  NegotiationTopupOrigin,
} from "../../services/negotiationBalance";
import { BannerFeedback } from "../BannerFeedback";
import { Button } from "../Button";
import { FloatContainer } from "../FloatContainer";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import {
  countdown,
  DEFAULT_MAX_TOPUP_CENTS,
  DEFAULT_MIN_TOPUP_CENTS,
  formatFeeRate,
  parseWholeReais,
  pendingTopupLabel,
  topupQuote,
} from "./negotiationBalanceFormat";
import { useTopupFlow } from "./useTopupFlow";
import "./NegotiationBalance.css";

export type RechargeModalViewProps = {
  /** The balance the company wants, in whole reais as typed. */
  amountDraft: string;
  /** Limits of that balance (the net), not of the Pix total. */
  limits: NegotiationTopupLimits;
  /**
   * The top-up fee Payments declares. The fee and the Pix total are shown
   * before any QR is generated, so without it no QR is offered.
   */
  topupFee?: NegotiationTopupFee;
  /** Payments accepts new top-ups. */
  topupsEnabled: boolean;
  /** Present once Payments answered the creation (or the read of a resumed QR). */
  topup?: NegotiationTopup;
  /** A QR left open earlier is being read. */
  resuming?: boolean;
  /**
   * QRs of the company still open (they count toward maxOpenTopups). Each
   * can be shown again to pay or cancel it; absent or empty lists nothing.
   */
  openTopups?: NegotiationPendingTopup[];
  onResumeTopup?: (id: string) => void;
  creating?: boolean;
  cancelling?: boolean;
  cancelRequested?: boolean;
  pollingIssue?: boolean;
  generationDelayed?: boolean;
  checking?: boolean;
  onRefreshTopup?: () => void;
  /**
   * An expired or removed QR may still be credited until this instant (epoch
   * ms): the modal keeps watching and warns before a second QR is generated.
   */
  lateReceiptUntilMs?: number;
  /** Application-authored copy only. */
  error?: string;
  /** Clock of the expiry countdown (epoch ms). */
  nowMs: number;
  onAmountChange: (draft: string) => void;
  onGenerate: () => void;
  onCancelTopup: () => void;
  onStartOver: () => void;
  onClose: () => void;
};

function Status({ children, busy = false }: { children: React.ReactNode; busy?: boolean }) {
  return (
    <p
      className="negotiationRechargeStatus"
      role="status"
      data-testid="negotiation-recharge-status"
    >
      {busy && <CircularProgress size={16} thickness={5} sx={{ color: "var(--brand-9)" }} />}
      <span>{children}</span>
    </p>
  );
}

function CopyCode({ value }: { value: string }) {
  const fieldId = React.useId();
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const [copy, setCopy] = React.useState<"idle" | "copied" | "failed">("idle");
  const resetTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  async function copyCode() {
    window.clearTimeout(resetTimer.current);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(value);
      setCopy("copied");
      resetTimer.current = window.setTimeout(() => setCopy("idle"), 3000);
    } catch {
      textRef.current?.select();
      setCopy("failed");
    }
  }

  return (
    <div className="negotiationRechargeField">
      <label htmlFor={fieldId}>Pix copia e cola</label>
      <textarea
        id={fieldId}
        ref={textRef}
        className="negotiationRechargeCode"
        data-testid="negotiation-recharge-copy-paste"
        readOnly
        rows={3}
        value={value}
        onFocus={(event) => event.currentTarget.select()}
      />
      <div className="negotiationBalanceModalActions">
        <Button callback={() => void copyCode()}>
          {copy === "copied" ? "Código copiado" : "Copiar código Pix"}
        </Button>
      </div>
      {copy === "failed" && (
        <span className="negotiationBalanceFieldError" role="alert">
          Não foi possível copiar automaticamente. O código está selecionado: copie com o teclado.
        </span>
      )}
    </div>
  );
}

/** "2%, mínimo R$ 0,20": the declared fee, as the fee line names it. */
function feeRateText(fee: NegotiationTopupFee): string {
  const rate = formatFeeRate(fee.bps);
  return fee.floorCents > 0 ? `${rate}, mínimo ${formatBRL(fee.floorCents)}` : rate;
}

/**
 * The gross-up before the QR: the balance chosen, the top-up fee on top of it
 * and the Pix total. Nothing here is an estimate: the balance receives exactly
 * the chosen amount, and the fee is not refundable.
 */
function TopupQuoteSummary({ fee, netCents }: { fee: NegotiationTopupFee; netCents: number }) {
  const quote = topupQuote(netCents, fee);
  if (!quote) return null;
  return (
    <>
      <dl className="negotiationRechargeSummary" data-testid="negotiation-recharge-quote">
        <div>
          <dt>Vai para o saldo</dt>
          <dd data-testid="negotiation-recharge-quote-net">{formatBRL(quote.netCents)}</dd>
        </div>
        <div>
          <dt>Tarifa de recarga ({feeRateText(fee)})</dt>
          <dd data-testid="negotiation-recharge-quote-fee">{formatBRL(quote.feeCents)}</dd>
        </div>
        <div className="negotiationRechargeSummaryTotal">
          <dt>Total do Pix</dt>
          <dd data-testid="negotiation-recharge-quote-total">{formatBRL(quote.chargeCents)}</dd>
        </div>
      </dl>
      <p className="negotiationBalanceHint" data-testid="negotiation-recharge-exact">
        O saldo recebe exatamente {formatBRL(quote.netCents)} quando o Pix for confirmado. A tarifa
        de recarga não é reembolsável.
      </p>
    </>
  );
}

function AmountForm(props: RechargeModalViewProps) {
  const fieldId = React.useId();
  const reais = parseWholeReais(props.amountDraft);
  const netCents = reais === null ? null : reais * 100;
  const inRange =
    netCents !== null &&
    netCents >= props.limits.minTopupCents &&
    netCents <= props.limits.maxTopupCents;

  // The fee is shown before the QR is generated, so a QR is never offered
  // without it. Payments declares the fee whenever it accepts top-ups.
  if (!props.topupsEnabled || !props.topupFee) {
    return (
      <p className="negotiationBalanceText" data-testid="negotiation-recharge-disabled">
        As recargas do Saldo de Negociações estão temporariamente indisponíveis.
      </p>
    );
  }

  return (
    <>
      <div className="negotiationRechargeField">
        <label htmlFor={fieldId}>Valor para o saldo (R$, inteiro)</label>
        <input
          id={fieldId}
          inputMode="numeric"
          autoComplete="off"
          value={props.amountDraft}
          placeholder={String(props.limits.minTopupCents / 100)}
          aria-invalid={props.amountDraft !== "" && !inRange}
          disabled={props.creating}
          data-testid="negotiation-recharge-amount"
          onChange={(event) => props.onAmountChange(event.target.value)}
        />
        <small className="negotiationBalanceHint">
          Mínimo {formatBRL(props.limits.minTopupCents)}; máximo{" "}
          {formatBRL(props.limits.maxTopupCents)}.
        </small>
      </div>
      {inRange ? (
        <TopupQuoteSummary fee={props.topupFee} netCents={netCents!} />
      ) : (
        <p className="negotiationBalanceHint" data-testid="negotiation-recharge-fee-rule">
          O Pix soma a tarifa de recarga ({feeRateText(props.topupFee)}) ao valor escolhido, e o
          saldo recebe exatamente o valor escolhido.
        </p>
      )}
      <p className="negotiationBalanceHint">
        O saldo paga apenas os lances. A veiculação no Átrio exige Pix Automático aprovado e
        cobrança-base semanal admitida.
      </p>
      <div className="negotiationBalanceModalActions">
        <Button
          callback={props.onGenerate}
          disabled={!inRange}
          loading={{ state: Boolean(props.creating), text: "Gerando o Pix…" }}
        >
          Gerar Pix
        </Button>
      </div>
      <OpenTopups {...props} />
    </>
  );
}

/**
 * The company's QRs still open. Without this a QR left behind by a closed
 * modal kept counting toward maxOpenTopups until it expired, and the limit
 * copy asked for something no screen offered.
 */
function OpenTopups({ openTopups, onResumeTopup, creating }: RechargeModalViewProps) {
  if (!onResumeTopup || !openTopups || openTopups.length === 0) return null;
  return (
    <div className="negotiationRechargeOpen" data-testid="negotiation-recharge-open">
      <p className="negotiationBalanceHint">
        Recargas em aberto: abra uma para pagar ou cancelar o QR. Cada QR deixa de contar quando
        expira.
      </p>
      <ul>
        {openTopups.map((open) => (
          <li key={open.id}>
            <span>
              Recarga de {formatBRL(open.netCents)} (Pix de {formatBRL(open.chargeCents)}) ·{" "}
              {pendingTopupLabel(open.state)}
            </span>
            <button
              type="button"
              className="negotiationRechargeSecondary"
              disabled={creating}
              aria-label={`Ver QR da recarga de ${formatBRL(open.netCents)}`}
              onClick={() => onResumeTopup(open.id)}
            >
              Ver QR
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopupScreen(props: RechargeModalViewProps & { topup: NegotiationTopup }) {
  const { topup } = props;
  const remaining = countdown(topup.expiresAt, props.nowMs);
  switch (topup.state) {
    case "requested":
    case "put_unknown":
      return props.generationDelayed ? (
        <>
          <Status>O Pix ainda está sendo processado</Status>
          <p className="negotiationBalanceHint" data-testid="negotiation-recharge-pending">
            Seu pedido continua salvo. Você pode consultar a situação aqui ou fechar e acompanhar
            esta mesma recarga em Recargas em aberto.
          </p>
          <div className="negotiationBalanceModalActions">
            <Button
              callback={() => props.onRefreshTopup?.()}
              disabled={!props.onRefreshTopup}
              loading={{ state: Boolean(props.checking), text: "Consultando…" }}
            >
              Atualizar situação
            </Button>
          </div>
        </>
      ) : (
        <Status busy>Gerando o Pix…</Status>
      );
    case "active":
      return (
        <>
          {/* The amounts Payments answered for this QR, which the QR carries. */}
          <p className="negotiationBalanceText" data-testid="negotiation-recharge-charge">
            Pix de {formatBRL(topup.chargeCents)}: {formatBRL(topup.netCents)} para o saldo e{" "}
            {formatBRL(topup.feeCents)} de tarifa de recarga. O saldo recebe exatamente{" "}
            {formatBRL(topup.netCents)}.
          </p>
          {remaining && topup.pixCopiaECola ? (
            <>
              <div className="negotiationRechargeQr" data-testid="negotiation-recharge-qr">
                <QRCodeSVG
                  value={topup.pixCopiaECola}
                  size={208}
                  level="M"
                  marginSize={4}
                  title="QR Code da recarga do Saldo de Negociações"
                />
              </div>
              <CopyCode value={topup.pixCopiaECola} />
              <p className="negotiationBalanceHint" data-testid="negotiation-recharge-countdown">
                Expira em {remaining}.
              </p>
            </>
          ) : (
            <p className="negotiationBalanceHint" data-testid="negotiation-recharge-deadline">
              O prazo deste QR terminou. Se o Pix já foi pago, o crédito ainda é confirmado aqui.
            </p>
          )}
          <Status busy>Aguardando pagamento…</Status>
          {props.cancelRequested ? (
            <p className="negotiationBalanceHint">
              Cancelamento pedido. O QR sai do ar assim que a Efí confirmar que não houve pagamento.
            </p>
          ) : (
            <div className="negotiationBalanceModalActions">
              <button
                type="button"
                className="negotiationRechargeSecondary"
                disabled={props.cancelling}
                onClick={props.onCancelTopup}
              >
                {props.cancelling ? "Cancelando…" : "Cancelar"}
              </button>
            </div>
          )}
        </>
      );
    case "receipt_pending":
      return <Status busy>Confirmando…</Status>;
    case "confirmed":
      return (
        <>
          {/* Only the credit Payments confirmed is shown as credited; it is
              exactly the chosen amount, and the fee never reaches the balance. */}
          <Status>
            {topup.creditedCents !== undefined
              ? `Saldo creditado: ${formatBRL(topup.creditedCents)}`
              : "Saldo creditado. Confira o valor no Saldo de Negociações."}
          </Status>
          <p className="negotiationBalanceHint" data-testid="negotiation-recharge-paid">
            Pix de {formatBRL(topup.chargeCents)}, com {formatBRL(topup.feeCents)} de tarifa de
            recarga.
          </p>
          <div className="negotiationBalanceModalActions">
            <Button callback={props.onClose}>Concluir</Button>
          </div>
        </>
      );
    case "expired":
    case "removed": {
      // Payments still credits a payment proven after the deadline, so a
      // second QR right away could be paid twice (both are credited).
      const late = props.lateReceiptUntilMs !== undefined && props.nowMs < props.lateReceiptUntilMs;
      return (
        <>
          <Status busy={late}>{late ? "QR expirado" : "QR expirado · gerar novo"}</Status>
          {late && (
            <p className="negotiationBalanceHint" data-testid="negotiation-recharge-late-receipt">
              Se você já pagou, o crédito ainda entra nos próximos minutos. Confira o extrato do
              banco antes de gerar outro Pix.
            </p>
          )}
          <div className="negotiationBalanceModalActions">
            <Button callback={props.onStartOver}>Gerar novo Pix</Button>
          </div>
        </>
      );
    }
    case "abandoned":
      return (
        <>
          <Status>Não foi possível gerar o Pix</Status>
          <div className="negotiationBalanceModalActions">
            <Button callback={props.onStartOver}>Tentar de novo</Button>
          </div>
        </>
      );
    case "unknown":
      return (
        <Status>
          Não foi possível acompanhar esta recarga aqui. Confira o histórico do Saldo de
          Negociações.
        </Status>
      );
    // Exhaustive over NegotiationTopupState.
    // no default
  }
}

/**
 * Recharge of the Saldo de Negociações by an Efí immediate Pix (QR or copia e
 * cola). Pure presentation: RechargeModal owns the requests and the polling.
 *
 * Gross-up (owner decision 2026-10-30): the company types the balance it
 * wants; before the QR the form shows the top-up fee Payments declares and
 * the Pix total (balance + fee), and the QR charges that total. Once Payments
 * proves the receipt the balance receives exactly the chosen amount, so
 * nothing is estimated and nothing is adjusted later. Nothing in transit is
 * ever shown as available.
 * @story args { amountDraft: "1000", limits: { minTopupCents: 1000, maxTopupCents: 5000000, maxOpenTopups: 3 }, topupFee: { bps: 200, floorCents: 20 }, topupsEnabled: true, nowMs: 1793284200000 }
 * @story state InvalidAmount { amountDraft: "5" }
 * @story state TopupsDisabled { topupsEnabled: false }
 * @story state Generating { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "put_unknown", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z" } }
 * @story state GenerationDelayed { generationDelayed: true, topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "put_unknown", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z" } }
 * @story state AwaitingPayment { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "active", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z", pixCopiaECola: "00020101021226830014br.gov.bcb.pix2561pix.storybook.invalid/cob/demo-recarga5204000053039865802BR5906DAKASA6009SAO PAULO62070503***6304ABCD" } }
 * @story state Confirming { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "receipt_pending", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z" } }
 * @story state Credited { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "confirmed", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z", creditedCents: 100000, confirmedAt: "2026-10-29T14:03:00Z" } }
 * @story state Expired { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "expired", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z" } }
 * @story state ExpiredAwaitingLateReceipt { lateReceiptUntilMs: 1793284800000, topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "expired", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T14:20:00Z", createdAt: "2026-10-29T13:20:00Z" } }
 * @story state Abandoned { topup: { id: "t1", channel: "cob", origin: "payments_tab", state: "abandoned", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z", createdAt: "2026-10-29T14:00:00Z" } }
 * @story state OpenLimit { error: "Há recargas em aberto; conclua ou cancele uma antes.", openTopups: [{ id: "t0", channel: "cob", state: "active", netCents: 100000, feeCents: 2000, chargeCents: 102000, expiresAt: "2026-10-29T15:00:00Z" }, { id: "t2", channel: "cob", state: "receipt_pending", netCents: 1000, feeCents: 20, chargeCents: 1020, expiresAt: "2026-10-29T14:50:00Z" }] }
 * @story state Resuming { resuming: true }
 */
export function RechargeModalView(props: RechargeModalViewProps) {
  return (
    <FloatContainer className="negotiationBalanceModal" close={props.onClose}>
      <div className="negotiationBalanceModalBody" data-testid="negotiation-recharge-modal">
        <h3 className="negotiationBalanceModalTitle">Recarregar o Saldo de Negociações</h3>
        <BannerFeedback type="error" open={Boolean(props.error)} message={props.error ?? ""} />
        {props.pollingIssue && (
          <p className="negotiationBalanceHint" role="status">
            A confirmação está demorando para chegar. Continuamos consultando.
          </p>
        )}
        {props.topup ? (
          <TopupScreen {...props} topup={props.topup} />
        ) : props.resuming ? (
          <Status busy>Carregando a recarga…</Status>
        ) : (
          <AmountForm {...props} />
        )}
      </div>
    </FloatContainer>
  );
}

export type RechargeModalProps = {
  origin: NegotiationTopupOrigin;
  /** Prefill in whole reais (the shortfall of a bid, rounded up). */
  initialAmountBRL?: number;
  /** Show this existing top-up (a QR left open) instead of the amount form. */
  resumeTopupId?: string;
  /** The company's open QRs, offered to be shown again from the amount form. */
  openTopups?: NegotiationPendingTopup[];
  onResumeTopup?: (id: string) => void;
  /** Payments refused a new QR because too many are open: re-read the list. */
  onTopupLimit?: () => void;
  limits?: NegotiationTopupLimits;
  /** The top-up fee Payments declares (balance.topupFee); no QR is offered without it. */
  topupFee?: NegotiationTopupFee;
  topupsEnabled: boolean;
  /** Payments requires the terms acceptance first; receives the typed amount (whole reais). */
  onTermsRequired: (amountBRL: number) => void;
  /** `credited` is true when the QR was paid and the balance credited. */
  onClose: (outcome: { credited: boolean }) => void;
};

const DEFAULT_LIMITS: NegotiationTopupLimits = {
  minTopupCents: DEFAULT_MIN_TOPUP_CENTS,
  maxTopupCents: DEFAULT_MAX_TOPUP_CENTS,
  maxOpenTopups: 3,
};

/**
 * Creates the top-up, polls GET /topups/:id every 2 s while the modal is open
 * and pauses generation after 30 s for a manual same-ID read. Polling stops
 * on a terminal state or on close; an expired or removed QR is
 * watched every 10 s until its late-receipt deadline.
 * @story skip request and polling lifecycle is covered by CI unit tests; see RechargeModalView for every visual state
 */
export function RechargeModal({
  origin,
  initialAmountBRL,
  resumeTopupId,
  openTopups,
  onResumeTopup,
  onTopupLimit,
  limits = DEFAULT_LIMITS,
  topupFee,
  topupsEnabled,
  onTermsRequired,
  onClose,
}: RechargeModalProps) {
  const flow = useTopupFlow({
    origin,
    initialAmountBRL,
    resumeTopupId,
    limits,
    onTermsRequired,
    onTopupLimit,
  });
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  // The clock runs for the countdown and, after expiry, until the
  // late-receipt deadline, so the warning gives way on time.
  const ticking =
    flow.topup?.state === "active" ||
    (flow.lateReceiptUntilMs !== undefined && nowMs < flow.lateReceiptUntilMs);
  React.useEffect(() => {
    if (!ticking) return;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);
  const credited = flow.topup?.state === "confirmed";
  return (
    <RechargeModalView
      amountDraft={flow.draft}
      limits={limits}
      topupFee={topupFee}
      topupsEnabled={topupsEnabled}
      topup={flow.topup}
      resuming={flow.resuming}
      openTopups={openTopups}
      onResumeTopup={onResumeTopup}
      creating={flow.creating}
      cancelling={flow.cancelling}
      cancelRequested={flow.cancelRequested}
      pollingIssue={flow.pollingIssue}
      generationDelayed={flow.generationDelayed}
      checking={flow.checking}
      onRefreshTopup={flow.refresh}
      lateReceiptUntilMs={flow.lateReceiptUntilMs}
      error={flow.error}
      nowMs={nowMs}
      onAmountChange={flow.setDraft}
      onGenerate={flow.generate}
      onCancelTopup={flow.cancel}
      onStartOver={flow.startOver}
      onClose={() => onClose({ credited })}
    />
  );
}
